import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RULE_LABELS, RULE_DESCRIPTIONS } from "@/lib/status";
import { Loader2, Save, ShieldAlert, AlertTriangle, Info } from "lucide-react";
import { toast } from "sonner";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { AiConfig } from "@/components/AiConfig";
import { AiRolloutReport } from "@/components/AiRolloutReport";

const THRESHOLDS = [
  { key: "overtime_weekly_hours", label: "Overtime weekly hours", suffix: "h" },
  { key: "travel_buffer_minutes", label: "Travel buffer", suffix: "min" },
  { key: "max_continuous_hours", label: "Max continuous shift", suffix: "h" },
  { key: "rounding_minutes", label: "Time rounding", suffix: "min" },
];

export default function RuleConfig() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.ruleConfig().then(setCfg).catch(() => toast.error("Load failed")); }, []);

  if (!cfg) return <div className="p-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;

  const setThreshold = (k, v) => setCfg((c) => ({ ...c, [k]: Number(v) }));
  const setSeverity = (code, sev) => setCfg((c) => ({ ...c, severity: { ...c.severity, [code]: sev } }));

  const save = async () => {
    setSaving(true);
    try { const res = await api.saveRuleConfig(cfg); setCfg(res); toast.success("Rules engine configuration saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-5 max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900 tracking-tight">Rules Engine Configuration</h1>
          <p className="text-xs text-slate-500 mt-0.5">Tune thresholds and decide which checks are hard blocks vs warnings — no rebuild required.</p>
        </div>
        <Button size="sm" className="h-9 text-xs" style={{ backgroundColor: "var(--brand)" }} onClick={save} disabled={saving} data-testid="save-rules">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-3.5 w-3.5 mr-1.5" />Save</>}
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 mt-4">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-3">Thresholds</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {THRESHOLDS.map((t) => (
            <div key={t.key}>
              <label className="text-[11px] font-medium text-slate-600">{t.label}</label>
              <div className="flex items-center gap-1 mt-1">
                <Input type="number" value={cfg[t.key]} onChange={(e) => setThreshold(t.key, e.target.value)} className="h-9 text-xs" data-testid={`threshold-${t.key}`} />
                <span className="text-[10px] text-slate-400">{t.suffix}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 mt-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-3">Geofencing (clock in/out)</div>
        <p className="text-[11px] text-slate-500 mb-3">Compare the worker's GPS at clock in/out against the shift address. Out-of-range events are flagged for review — they never block clocking.</p>
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex gap-2">
            {[["on", true, "Enabled"], ["off", false, "Disabled"]].map(([id, v, lbl]) => (
              <button key={id} onClick={() => setCfg((c) => ({ ...c, geofence_enabled: v }))} data-testid={`geofence-${id}`}
                className={`text-xs font-semibold px-3 py-2 rounded-md border transition-colors ${
                  (cfg.geofence_enabled !== false) === v ? "bg-brand text-white border-brand" : "bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100"}`}>
                {lbl}
              </button>
            ))}
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-600">Allowed radius</label>
            <div className="flex items-center gap-1 mt-1">
              <Input type="number" value={cfg.geofence_metres ?? 500} onChange={(e) => setCfg((c) => ({ ...c, geofence_metres: Number(e.target.value) }))}
                className="h-9 text-xs w-28" data-testid="geofence-metres" disabled={cfg.geofence_enabled === false} />
              <span className="text-[10px] text-slate-400">metres</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 mt-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-3">Live monitoring</div>
        <p className="text-[11px] text-slate-500 mb-3">On the dashboard's "Active shifts right now" report, flag a shift as a late start when an assigned worker still hasn't clocked in this many minutes after the rostered start time.</p>
        <div>
          <label className="text-[11px] font-medium text-slate-600">Late-start grace</label>
          <div className="flex items-center gap-1 mt-1">
            <Input type="number" min="0" value={cfg.late_start_grace_minutes ?? 10}
              onChange={(e) => setCfg((c) => ({ ...c, late_start_grace_minutes: Number(e.target.value) }))}
              className="h-9 text-xs w-28" data-testid="late-start-grace" />
            <span className="text-[10px] text-slate-400">minutes after start</span>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 mt-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-3">Roster week cycle</div>
        <p className="text-[11px] text-slate-500 mb-3">The roster board shows a rotating 4-week label (Week 1→2→3→4→1…). Set the Friday that Week 1 begins on. It must be a Friday (weeks run Fri–Thu).</p>
        <div>
          <label className="text-[11px] font-medium text-slate-600">Week 1 start (Friday)</label>
          <Input type="date" value={(cfg.roster_week_anchor || "2026-08-21").slice(0, 10)} onChange={(e) => setCfg((c) => ({ ...c, roster_week_anchor: e.target.value }))}
            className="h-9 text-xs w-44 mt-1" data-testid="roster-week-anchor" />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 mt-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-3">Office contact (incident escalation)</div>
        <p className="text-[11px] text-slate-500 mb-3">Shown to workers when they log a High or Critical incident, prompting them to call the office immediately.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-[11px] font-medium text-slate-600">During office hours</label>
            <Input value={cfg.office_phone_business ?? ""} onChange={(e) => setCfg((c) => ({ ...c, office_phone_business: e.target.value }))}
              className="h-9 text-xs mt-1" data-testid="office-phone-business" placeholder="e.g. 1300 000 000" />
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-600">After hours</label>
            <Input value={cfg.office_phone_afterhours ?? ""} onChange={(e) => setCfg((c) => ({ ...c, office_phone_afterhours: e.target.value }))}
              className="h-9 text-xs mt-1" data-testid="office-phone-afterhours" placeholder="e.g. 07 0000 0000" />
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-600">After-hours line PIN</label>
            <Input value={cfg.office_afterhours_pin ?? ""} onChange={(e) => setCfg((c) => ({ ...c, office_afterhours_pin: e.target.value }))}
              className="h-9 text-xs mt-1" data-testid="office-afterhours-pin" placeholder="e.g. 1234" />
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 mt-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-3">Worker note visibility</div>
        <p className="text-[11px] text-slate-500 mb-3">What shift-note history a support worker can see on a participant they support.</p>
        <div className="flex gap-2">
          {[["all", "All workers' notes"], ["own", "Only their own notes"]].map(([v, lbl]) => (
            <button key={v} onClick={() => setCfg((c) => ({ ...c, worker_note_history: v }))}
              data-testid={`worker-notes-${v}`}
              className={`text-xs font-semibold px-3 py-2 rounded-md border transition-colors ${
                (cfg.worker_note_history || "all") === v ? "bg-brand text-white border-brand" : "bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100"}`}>
              {lbl}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 mt-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-3">Check severities</div>
        <div className="space-y-1.5">
          {Object.keys(cfg.severity || {}).map((code) => {
            const sev = cfg.severity[code];
            return (
              <div key={code} className="flex items-center justify-between rounded-md border border-slate-100 px-3 py-2" data-testid={`severity-row-${code}`}>
                <span className="text-xs text-slate-700 flex items-center gap-1.5">
                  {RULE_LABELS[code] || code}
                  {RULE_DESCRIPTIONS[code] && (
                    <TooltipProvider delayDuration={100}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button type="button" aria-label={`What is ${RULE_LABELS[code] || code}?`} data-testid={`severity-info-${code}`}
                            className="text-slate-400 hover:text-indigo-600 transition-colors">
                            <Info className="h-3.5 w-3.5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-xs text-xs leading-snug">
                          {RULE_DESCRIPTIONS[code]}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </span>
                <div className="flex gap-1">
                  <button onClick={() => setSeverity(code, "block")} data-testid={`severity-${code}-block`}
                    className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${sev === "block" ? "bg-red-600 text-white" : "bg-slate-100 text-slate-400"}`}>
                    <ShieldAlert className="h-3 w-3" />Block
                  </button>
                  <button onClick={() => setSeverity(code, "warning")} data-testid={`severity-${code}-warning`}
                    className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${sev === "warning" ? "bg-amber-500 text-white" : "bg-slate-100 text-slate-400"}`}>
                    <AlertTriangle className="h-3 w-3" />Warn
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <AiConfig />
      <AiRolloutReport />
    </div>
  );
}
